import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ICellRendererParams } from '@ag-grid-community/core'
import { DataTable, DataTablePagination } from 'components/common'
import { useQuery } from 'hooks'
import useModal from 'hooks/useModal'
import { fetchGenericLogs } from 'utils/api/logsApi'
import { calculateOffset } from 'utils/helpers/pagination'
import { filterEmptyValues, FilterMeta } from 'utils/helpers'
import { FILTER_FIELD_GENERIC_LOG } from './genericLogFilterForm'
import GenericLogDetailsModal from './genericLogDetailsModal'
import { getColumns } from './utils'

type Props = {
  onFilterMeta?: (filterMeta: FilterMeta) => void
}

const GenericLogTable = ({ onFilterMeta }: Props) => {
  const [logResponse, setLogResponse] = useState<any>({})
  const [page, setPage] = useState<number>(0)
  const [selectedLog, setSelectedLog] = useState<any | null>(null)

  const { getAllQueries } = useQuery()
  const { search } = useLocation()
  const { isOpen, openModal, closeModal } = useModal()

  const handleViewLog = (params: ICellRendererParams) => {
    setSelectedLog(params.data)
    openModal()
  }

  const handleCloseModal = () => {
    closeModal()
    setSelectedLog(null)
  }

  const columns = useMemo(
    () => getColumns({ page, onView: handleViewLog }),
    [page],
  )

  const getLogs = async () => {
    try {
      const queries = getAllQueries()
      const { limit, page: pageParam } = queries
      const filters = filterEmptyValues(queries[FILTER_FIELD_GENERIC_LOG] || {})

      const { data } = await fetchGenericLogs({
        limit: limit || 10,
        page: pageParam || 1,
        ...filters,
      })

      setPage(calculateOffset(data))
      setLogResponse(data)
      if (data?.filterMeta) {
        onFilterMeta?.(data.filterMeta)
      }
    } catch (error) {
      setLogResponse({ docs: [] })
    }
  }

  useEffect(() => {
    getLogs()
  }, [search])

  return (
    <>
      <DataTable columns={columns} rows={logResponse?.docs} identifier="_id" />

      <GenericLogDetailsModal
        log={selectedLog}
        isOpen={isOpen}
        onClose={handleCloseModal}
      />

      <DataTablePagination
        currentPage={logResponse?.page}
        totalPages={logResponse?.totalPages}
        hasNextPage={logResponse?.hasNextPage}
        hasPrevPage={logResponse?.hasPrevPage}
        limit={logResponse?.limit}
        nextPage={logResponse?.nextPage}
        prevPage={logResponse?.prevPage}
      />
    </>
  )
}

export default GenericLogTable
